import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'

import CircularProgress from '../../atoms/feedback/circularProgress'
import useStyles from './generateSummaryStyle'
import Button from '../../atoms/inputs/button'
import BoltIcon from '../../atoms/icons/bolt'
import { useGenerateSummary } from '../../../hooks/useGenerateSummary'
import { useAutomaticSummarization } from '../../../hooks/useAutomaticSummarization'
import { AutomaticallySummarize } from './automaticallySummarize'
import { setNewAiMessage } from '../../../redux/conversations/actions'

export const GenerateSummary = ({ messageId, conversationId, isDisabled }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const size = useSelector((state) => state.preferences)
  const activeConversation = useSelector((state) => state.activeConversation)
  const messageStore = useSelector(
    (state) => state.conversations.messages[activeConversation]
  )
  const conversation = useSelector(
    (state) => state.conversations.conversations[activeConversation]
  )
  let useStylesParams = { size: size }
  const classes = useStyles(useStylesParams)

  const { isAutomaticallyGenerateSummaryEnabled } = useAutomaticSummarization()
  const [isLoading, setIsLoading] = useState(false)

  const handleGenerateSummary = useCallback(async () => {
    setIsLoading(true)
    await generateSummary()
  }, [messageId, conversationId, dispatch])

  const onSuccess = useCallback(
    ({ summary, keywords }) => {
      const messageIndex = messageStore.findIndex(
        (msg) => msg.currentId === messageId
      )
      if (messageIndex > -1) {
        dispatch(
          setNewAiMessage({
            message: {
              ...messageStore[messageIndex],
              summary,
              summaryKeywords: keywords,
            },
            chatId: activeConversation,
            messageId,
          })
        )
      }
      setIsLoading(false)
    },
    [conversation, messageStore]
  )

  const onError = useCallback(() => {
    setIsLoading(false)
    enqueueSnackbar(t('summary.Error summarizing conversation'), {
      variant: 'error',
      autoHideDuration: 3000,
    })
  }, [setIsLoading, enqueueSnackbar, t])

  const { generateSummary, isLoading: isLoadingSummary } = useGenerateSummary({
    conversationId,
    messageId,
    onSuccess,
    onError,
  })

  const disabled = useMemo(
    () => isLoadingSummary || isDisabled,
    [isLoadingSummary, isDisabled]
  )

  useEffect(() => {
    if (!isAutomaticallyGenerateSummaryEnabled || isDisabled) return
    handleGenerateSummary()
  }, [isAutomaticallyGenerateSummaryEnabled, handleGenerateSummary, isDisabled])

  if (isLoading) {
    return (
      <div className={classes.loadingChild}>
        <CircularProgress size={24} />
        <p>{t('summary.readingInProgress')}</p>
      </div>
    )
  }

  return (
    <>
      <Button
        size="large"
        variant="contained"
        fullWidth
        color="primary"
        onClick={handleGenerateSummary}
        style={{ borderRadius: 50 }}
        endIcon={<BoltIcon opacity={disabled ? 0.13 : 1} />}
        disabled={disabled}
      >
        {t('summary.Summarize conversation')}
      </Button>
      <AutomaticallySummarize isDisabled={disabled} />
    </>
  )
}
